import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Send, Save, RefreshCw, Ban } from "lucide-react";

import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { useToast } from "@crm-src/hooks/use-toast";
import usePermissions from "@crm-src/hooks/usePermissions";
import {
  getColdEmail,
  generateColdEmail,
  updateColdEmail,
  approveColdEmail,
  skipColdEmail,
  getColdEmailSettings,
  type ColdEmailBatch,
} from "@crm-src/lib/cold-email-api";
import { coldStatusChip, coldRecipientChip, ensureFullBody } from "@crm-src/lib/cold-email-format";
import { coldEmailPath } from "@crm-src/lib/cold-email-routes";
import { BackButton } from "@/components/ui/back-button";

export function ColdEmailReviewPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const batchId = Number(id);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isSuperAdmin } = usePermissions();
  const canApprove = isSuperAdmin();

  const [batch, setBatch] = useState<ColdEmailBatch | null>(null);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState<"" | "generate" | "save" | "approve" | "skip">("");
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [sender, setSender] = useState({ name: "XOP Networks", address: "sgupta@xopnetworks.com" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const b = await getColdEmail(batchId);
      setBatch(b);
      setSubject(b.subject ?? "");
      setBody(b.body ? ensureFullBody(b.body) : "");
    } catch {
      toast({ title: "Could not load this cold email", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }, [batchId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    void getColdEmailSettings()
      .then((s) => setSender({ name: s.from_name, address: s.from_address }))
      .catch(() => undefined);
  }, []);

  const apply = (b: ColdEmailBatch): void => {
    setBatch(b);
    setSubject(b.subject ?? "");
    setBody(b.body ? ensureFullBody(b.body) : "");
  };

  const handleGenerate = async (): Promise<void> => {
    setBusy("generate");
    try {
      apply(await generateColdEmail(batchId));
      toast({ title: "Draft ready", description: "Review the email, edit if needed, then approve." });
    } catch {
      toast({ title: "Generation failed", description: "The AI service may be busy. Try again.", variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  const handleSave = async (): Promise<void> => {
    setBusy("save");
    try {
      apply(await updateColdEmail(batchId, { subject, body }));
      toast({ title: "Draft saved" });
    } catch {
      toast({ title: "Could not save the draft", variant: "destructive" });
    } finally {
      setBusy("");
    }
  };

  const handleApprove = async (): Promise<void> => {
    // Save any pending edits first so we send exactly what the CEO sees.
    if (batch && (subject !== (batch.subject ?? "") || body !== (batch.body ?? ""))) {
      await updateColdEmail(batchId, { subject, body });
    }
    apply(await approveColdEmail(batchId));
    toast({ title: "Approved", description: "The email is now sending to every contact at this company." });
  };

  const handleSkip = async (): Promise<void> => {
    setBusy("skip");
    try {
      await skipColdEmail(batchId);
      toast({ title: "Skipped", description: "This company will not be emailed." });
      navigate(coldEmailPath());
    } catch {
      toast({ title: "Could not skip this batch", variant: "destructive" });
      setBusy("");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!batch) {
    return (
      <Box sx={{ p: 3 }}>
        <BackButton label="Back to queue" onClick={() => navigate(coldEmailPath())} />
        <Alert severity="error" sx={{ mt: 3 }}>
          This cold email could not be found.
        </Alert>
      </Box>
    );
  }

  const chip = coldStatusChip(batch.status);
  const editable = batch.status !== "sending" && batch.status !== "sent";
  const hasDraft = Boolean(batch.subject && batch.body);
  const canSendNow = editable && hasDraft && batch.recipients_count > 0;

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3, maxWidth: 1100, mx: "auto" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <BackButton label="Queue" onClick={() => navigate(coldEmailPath())} />
        <Box>
          <Typography component="h2" sx={{ fontSize: "1.5rem", fontWeight: 700 }}>
            {batch.company_name}
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            {[batch.industry, batch.company_domain].filter(Boolean).join(" · ") || "Cold outreach"}
          </Typography>
        </Box>
        <Chip label={chip.label} color={chip.color} variant="outlined" sx={{ ml: "auto" }} />
      </Box>

      {batch.status === "failed" && batch.generation_error && (
        <Alert severity="error">Last draft failed: {batch.generation_error}</Alert>
      )}
      {batch.status === "sent" && (
        <Alert severity="success">
          Sent to {batch.sent_count} of {batch.recipients_count} contacts
          {batch.failed_count > 0 ? ` (${batch.failed_count} failed)` : ""}
          {batch.approved_by ? ` · approved by ${batch.approved_by}` : ""}.
        </Alert>
      )}
      {batch.status === "sending" && (
        <Alert severity="info">This email is sending to every contact at {batch.company_name}.</Alert>
      )}

      <Card variant="outlined">
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ fontWeight: 600 }}>Email</Typography>
          {!hasDraft && batch.status !== "generating" && (
            <Alert severity="info">
              No draft yet. Click <strong>Generate draft</strong> to have the AI write a warm,
              researched email for this company.
            </Alert>
          )}
          <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", color: "text.secondary" }}>
            <Typography variant="body2">
              <strong>From</strong> {sender.name} &lt;{sender.address}&gt;
            </Typography>
            <Typography variant="body2">
              <strong>To</strong> {batch.recipients_count} contact{batch.recipients_count === 1 ? "" : "s"}
            </Typography>
          </Box>
          <TextField
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={!editable || busy !== ""}
            fullWidth
          />
          <TextField
            label="Email"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={!editable || busy !== ""}
            fullWidth
            multiline
            minRows={12}
            helperText="This is the exact email. Keep {{full_name}} where the contact's name should go — it becomes their name; use {{first_name}} for just the first name."
          />

          <Divider />
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
            <Button
              variant="outlined"
              startIcon={busy === "generate" ? <CircularProgress size={16} /> : <RefreshCw size={16} />}
              disabled={!editable || busy !== ""}
              onClick={() => void handleGenerate()}
            >
              {hasDraft ? "Regenerate" : "Generate draft"}
            </Button>
            <Button
              variant="outlined"
              startIcon={busy === "save" ? <CircularProgress size={16} /> : <Save size={16} />}
              disabled={!editable || busy !== "" || !hasDraft}
              onClick={() => void handleSave()}
            >
              Save draft
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="outlined"
              color="warning"
              startIcon={<Ban size={16} />}
              disabled={!editable || busy !== ""}
              onClick={() => void handleSkip()}
            >
              Skip
            </Button>
            {canApprove ? (
              <Button
                variant="contained"
                startIcon={<Send size={16} />}
                disabled={!canSendNow || busy !== ""}
                onClick={() => setConfirmApprove(true)}
              >
                Approve &amp; send
              </Button>
            ) : (
              <Button variant="contained" startIcon={<Send size={16} />} disabled>
                CEO approval required
              </Button>
            )}
          </Stack>
          {!canApprove && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Only a super admin (the CEO) can approve and send. You can draft and edit here.
            </Typography>
          )}
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            Recipients ({batch.recipients?.length ?? batch.recipients_count})
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(batch.recipients ?? []).map((r) => {
                  const rc = coldRecipientChip(r.status);
                  return (
                    <TableRow key={r.id}>
                      <TableCell>{`${r.name ?? ""} ${r.surname ?? ""}`.trim() || "—"}</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{r.email}</TableCell>
                      <TableCell sx={{ color: "text.secondary" }}>{r.job_title || "—"}</TableCell>
                      <TableCell>
                        <Chip size="small" label={rc.label} color={rc.color} variant="outlined" />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <ConfirmDialog
        isOpen={confirmApprove}
        onClose={() => setConfirmApprove(false)}
        title="Approve and send this cold email?"
        description={`This will send the email to all ${batch.recipients_count} contact(s) at ${batch.company_name}. This cannot be undone.`}
        variant="warning"
        confirmLabel="Approve & send"
        onConfirm={async () => {
          await handleApprove();
        }}
      />
    </Box>
  );
}

export default ColdEmailReviewPage;
